import {observable, action} from "mobx";
import { Check } from "@sparkware/app-utils";
import { ServiceBase } from "../ServiceBase";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import { IJackpotsWithStateService, IJackpotData, JackpotThemeType } from "./jackpots-with-state.service.interface";
import { IJackpotHandler } from "./jackpot-handler/jackpot-handler.interface";
import { JackpotHandler } from "./jackpot-handler/jackpot-handler";
import {JackpotType, JACKPOT_STATE} from '../../../common/const/jackpot_map_ids';
import { ThemeName } from "../../themes/architecture/theme-names";

export class JackpotsWithStateService extends ServiceBase implements IJackpotsWithStateService {
    /**
     * In order to accomodate Blue Ribbon we moved from number index to string index in the currentJackpots record
     */
    @observable.ref
    public currentJackpots: Record<string, IJackpotHandler> = {};

    constructor(serviceFactory: IServiceFactoryExtended) {
        super(serviceFactory);

        const jackpotsWithState = this.services.sdk.getJackpotsWithState();
        this.updateCurrentJackpots(this.readJackpotsData(jackpotsWithState));
        this.subscribe();
    }

    /**
     * When the LIVE_FEEDS_STATE_JACKPOTS_UPDATED from CGP is fired, the jackpots must be updated
     */
    private subscribe() {
        this.services.sdk.events.onJackpotsWithStateUpdate((jackpotsWithState) => {
            this.updateCurrentJackpots(this.readJackpotsData(jackpotsWithState));
        });
    }

    @action
    private updateCurrentJackpots(jackpots: IJackpotData[], useGradualAmountIncrease: boolean = true) {
        let jackpotsAddition: Record<string, IJackpotHandler> = {};
        jackpots.forEach(jpData => {
            if (this.currentJackpots[jpData.jackpotId]) {
                this.currentJackpots[jpData.jackpotId].setJackpotData(jpData, useGradualAmountIncrease);
            } else {
                jackpotsAddition[jpData.jackpotId] = new JackpotHandler(this.services);
                jackpotsAddition[jpData.jackpotId].setJackpotData(jpData, useGradualAmountIncrease);
            }
        });
        if (!Check.isEmpty(jackpotsAddition)) {
            this.currentJackpots = {
                ...this.currentJackpots,
                ...jackpotsAddition
            };
        }
    }


    /**
     * Because cgp relies on a lot of js, "any" types, un-strict typescript and it's a separate system
     * data must be validated before use.
     */
    private readJackpotsData(rawJackpots: unknown): IJackpotData[] {

        let jackpots: IJackpotData[] = [];
        if (Check.isNullOrUndefined(rawJackpots) || !Check.isObject(rawJackpots)) {
            return jackpots;
        }

        Object.keys(rawJackpots).forEach(jpId => {
            try {
                let rawJackpot: any = (rawJackpots as any)[jpId].Jackpot;

                if (Check.isNullOrUndefined(rawJackpot) || Check.isEmpty(rawJackpot)) {
                    return;
                }

                if (!Check.isNumber(Number(rawJackpot["AmountInCents"]))) {
                    throw new Error("Jackpot AmountInCents missing or corrupted. Data received: " + rawJackpot);
                }
                if (!rawJackpot["JackpotId"]) {
                    throw new Error("Jackpot JackpotId missing or corrupted. Data received: " + rawJackpot);
                }
                if (!Check.isNumber(Number(rawJackpot["JackpotState"]))) {
                    throw new Error("Jackpot JackpotState missing or corrupted. Data received: " + rawJackpot);
                }
                if (!Check.isNumber(Number(rawJackpot["LastWinAmount"]))) {
                    throw new Error("Jackpot LastWinAmount missing or corrupted. Data received: " + rawJackpot);
                }
                if (Check.isNullOrUndefined(rawJackpot["MustHitTime"])) {
                    throw new Error("Jackpot MustHitTime missing. Data received: " + rawJackpot);
                }
                const mustHitTime = new Date(String(rawJackpot["MustHitTime"]));
                // mustHitTime must be a Date formatted string: Ex: "2020-08-04T15:47:43.683Z"
                if (mustHitTime.toString() === "Invalid Date") {
                    throw new Error("Jackpot MustHitTime corrupted");
                }

                jackpots.push({
                    amountInCents: Number(rawJackpot["AmountInCents"]),
                    jackpotId: String(rawJackpot["JackpotId"]),
                    jackpotState: this.readJackpotState(Number(rawJackpot["JackpotState"])),
                    lastWinAmount: Number(rawJackpot["LastWinAmount"]),
                    mustHitTime
                });
            } catch (e) {
                this.services.logger.error("Error occurred reading jackpot data", e);
            }
        });

        return jackpots;
    }

    private readJackpotState(state: number) {
        switch (state) {
            case 1:
                return JACKPOT_STATE.Cold;
            case 2:
                return JACKPOT_STATE.Regular;
            case 3:
            case 4:
            case 5:
                return JACKPOT_STATE.Hot;
            default:
                return JACKPOT_STATE.Regular;
        }
    }

    /**
     * the 888 theme is dark and so it is the widget. So the texts are light
     * the 777 theme is light and so it is the widget. So the texts are dark
     * But, the Hourly Captain 8 widget is dark on all skins so we need to adapt
     */
    public getThemeType(jpType: JackpotType): JackpotThemeType {
        if (this.services.theme.currentTheme.themeName === ThemeName.Casino777) {
            if (jpType === JackpotType.HourlyCaptain8) {
                return JackpotThemeType.Dark;
            }
            return JackpotThemeType.Light;
        }
        return JackpotThemeType.Dark;
    }
}
